import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { BookingListFilters, CreateBookingData, bookingsApi } from '@/services';

export const bookingsKeys = {
  all: ['bookings'] as const,
  rooms: ['bookings', 'rooms'] as const,
  activeSchoolYear: ['bookings', 'active-school-year'] as const,
  tabletPool: ['bookings', 'tablet-pool'] as const,
  availability: (date: string, periodId: string) => ['bookings', 'availability', date, periodId] as const,
  dayAvailability: (date: string) => ['bookings', 'availability', 'day', date] as const,
  limits: ['bookings', 'limits'] as const,
  upcoming: (limit: number) => ['bookings', 'upcoming', limit] as const,
  mine: ['bookings', 'mine'] as const,
  list: (filters: BookingListFilters) => ['bookings', 'list', filters] as const,
  detail: (id: string) => ['bookings', 'detail', id] as const,
};

export function useBookableRoomsQuery() {
  return useQuery({ queryKey: bookingsKeys.rooms, queryFn: () => bookingsApi.bookableRooms() });
}

export function useActiveSchoolYearQuery() {
  return useQuery({
    queryKey: bookingsKeys.activeSchoolYear,
    queryFn: () => bookingsApi.activeSchoolYear(),
    retry: false,
  });
}

export function useTabletPoolQuery() {
  return useQuery({ queryKey: bookingsKeys.tabletPool, queryFn: () => bookingsApi.tabletPool() });
}

export function useSetTabletPoolMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (totalCount: number) => bookingsApi.setTabletPool(totalCount),
    onSuccess: () => qc.invalidateQueries({ queryKey: bookingsKeys.all }),
  });
}

export function useBookingAvailabilityQuery(date: string, periodId: string, enabled = true) {
  return useQuery({
    queryKey: bookingsKeys.availability(date, periodId),
    queryFn: () => bookingsApi.availability(date, periodId),
    enabled: enabled && !!date && !!periodId,
  });
}

/** The whole day's grid for one date. */
export function useBookingDayAvailabilityQuery(date: string, enabled = true) {
  return useQuery({
    queryKey: bookingsKeys.dayAvailability(date),
    queryFn: () => bookingsApi.dayAvailability(date),
    enabled: enabled && !!date,
  });
}

export function useBookingLimitsQuery() {
  return useQuery({ queryKey: bookingsKeys.limits, queryFn: () => bookingsApi.limits() });
}

export function useUpcomingBookingsQuery(limit = 5) {
  return useQuery({ queryKey: bookingsKeys.upcoming(limit), queryFn: () => bookingsApi.upcoming(limit) });
}

export function useCreateBookingMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateBookingData) => bookingsApi.create(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: bookingsKeys.all }),
  });
}

export function useMyBookingsQuery() {
  return useQuery({ queryKey: bookingsKeys.mine, queryFn: () => bookingsApi.listMine() });
}

export function useAllBookingsQuery(filters: BookingListFilters = {}) {
  return useQuery({ queryKey: bookingsKeys.list(filters), queryFn: () => bookingsApi.listAll(filters) });
}

/** One booking on its own address — what the booking's page reads. */
export function useBookingQuery(id?: string) {
  return useQuery({
    queryKey: bookingsKeys.detail(id ?? ''),
    queryFn: () => bookingsApi.detail(id as string),
    enabled: Boolean(id),
  });
}

export function useCancelBookingMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => bookingsApi.cancel(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: bookingsKeys.all }),
  });
}
